// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useState } from 'react'
import { IonPage, useIonViewWillLeave } from '@ionic/react'
import { useSession, SectionPage } from '../lib'
import { ProcurementClient } from '../api'
import { PurchaseOrder, PurchaseOrderDTO } from '../models'
import PurchaseOrderCreate from './PurchaseOrderCreate'
import PurchaseOrderAddProducts from './PurchaseOrderAddProducts'
import PurchaseOrderReview from './PurchaseOrderReview'
import PurchaseOrderPlaced from './PurchaseOrderPlaced'


interface CreatePurchaseOrderProps {}


const CreatePurchaseOrder: React.FC<CreatePurchaseOrderProps> = () => {
  const {ready, setError, setLoading} = useSession()
  const [stage, setStage] = useState<string>("create")
  const [orderState, setOrderState] = useState<PurchaseOrderDTO>(PurchaseOrder.restore())
  const client = new ProcurementClient()
  const order = new PurchaseOrder(client, orderState, setOrderState)

  let component: React.ReactElement | null
  switch (stage) {
    case "create":
      component = (<PurchaseOrderCreate
        onSubmit={() => setStage("products")}
        order={order}
        ready={ready}
      />)
      break
    case "done":
      component = (<PurchaseOrderPlaced order={order}/>)
      break
    case "products":
      component = (<PurchaseOrderAddProducts
        order={order}
        onPrevious={() => setStage("create")}
        onSubmit={() => { setStage("review")}}
      />)
      break
    case "review":
      component = (
        <PurchaseOrderReview
          order={order}
          onPrevious={() => setStage("products")}
          onSubmit={() => {
            setLoading(true)
            order.place()
              .then(() => setStage("done"))
              .catch(() => setError(true))
              .finally(() => setLoading(false))
          }}
        />
      )
      break
    default:
      component = null
  }

  useIonViewWillLeave(() => {
    setOrderState({} as PurchaseOrderDTO)
    setStage("create")
  })

  return (order === null) ? null : (
    <SectionPage>{component}</SectionPage>
  )
}


export { CreatePurchaseOrder }